// diversificationsApi.ts

import { Role } from '@/models/AccountEnums';
import { advisorScopeMissingError } from '@/utils/advisorScoping';
import { buildRoleAwarePrefix, isAdvisorRole } from '@/utils/api';
import { mergeViewingTagIntoArg } from '@/utils/viewingInvestorTagMerge';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  createApi,
  type TypedUseQuerySubscriptionOptions,
} from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';
import { RecordResponse } from '../models/ServiceTypes';
import {
  buildAccountDiversificationRequestParams,
  getAccountDiversificationCacheTagId,
} from './accountDiversificationQueryUtils';
import { waServicesQuery } from './baseQueries';
import {
  confirmedViewingTagSelection,
  selectQueryViewingTag,
} from './selectQueryViewingTag';
import { WARootState } from './store';

type ApiRoleSegment = string;
type QueryParams = Record<string, string | number | boolean | undefined>;
type DiversificationDataById = Partial<Record<number, DiversificationResponse>>;
type DiversificationLevelProperty = `DiversificationId${string}`;
type DiversificationItemWithLevels = DiversificationItem &
  Record<DiversificationLevelProperty, number>;

function getApiRoleSegment(state: WARootState): ApiRoleSegment {
  return state.auth.currentRole ?? 'investor';
}

// ======================
// 1. TypeScript Types
// ======================

export interface DiversificationCategory {
  DiversificationCategoryId: number;
  Name: string;
  ParentDiversificationId: number;
}

export type DiversificationCategoriesResponse =
  RecordResponse<DiversificationCategory>;

// HistoricalDiversification request
export interface HistoricalDiversificationRequest {
  startDate: string;
  endDate: string;
  diversificationId: number;
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  ExcludedInstitutions?: number[];
}

export interface HistoricalDiversificationDataPoint {
  Date: string;
  Value: number;
}

export interface HistoricalDiversificationCategory {
  name: string;
  id: number;
  color: string;
  data: HistoricalDiversificationDataPoint[];
  drilldown: boolean;
  /** Stacking position; the API can leave it out, which sorts first. */
  order?: number;
}

export interface HistoricalDiversificationResponse {
  Records: HistoricalDiversificationCategory[];
  TotalLength: number;
}

// Diversification request
export interface DiversificationRequest {
  asOfDate: string;
  diversificationId: number;
  categoryId?: number | null;
  InvestorId?: number;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  ExcludedInstitutions?: number[];
  InvestorTagId?: number;
  TagId?: number;
  sort?: {
    Property: string;
    Direction: string;
    Order: number;
  };
}

type DiversificationViewingTagArg = {
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
};

export function mergeViewingInvestorTagIntoDiversificationArg<
  T extends DiversificationViewingTagArg,
>(arg: T, selection: ViewingTagSelection): T {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildPortfolioDiversificationSubscriptionArg<
  T extends DiversificationViewingTagArg,
>(arg: T, selection: ViewingTagSelection): T {
  return mergeViewingInvestorTagIntoDiversificationArg(arg, selection);
}

export interface DiversificationItem {
  TargetPercent?: number; // 69.0;
  TargetAmount?: number; // 8346355.05710885806953;
  IsParent?: boolean;
  Level?: number;
  DifferencePercent?: number;
  DifferenceValue?: number;
  InRange?: boolean;
  Steps?: string;
  DiversificationName?: string;
  DiversificationId: number;
  AccountName?: string;
  name: string;
  InstitutionName?: string;
  PercentOfHolding: number;
  Price: number;
  TotalHoldingValue: number;
  Quantity: number;
  ParentId: number;
  CategoryId: number;
  y: number;
  percent: number;
  id: number;
  drilldown: boolean;
  order: number;
  color: string;
}

interface FlattenedDiversificationRow extends DiversificationItem {
  // For convenience, store a list of all ancestor names
  // (including the current record's name as the last element)
  ancestorNames: string[];
}

export interface SummedValue {
  Property: string;
  Value: number;
}

export interface DiversificationResponse {
  Records: DiversificationItem[];
  TotalLength: number;
  SummedValues: SummedValue[];
  maxDepth: number;
}

export interface DiversificationHoldingItem {
  SubDiversificationId: number;
  SubDiversificationName: string;
  DiversificationName: string;
  DiversificationId: number;
  CostBasis: number;
  AccountName: string;
  name: string;
  InstitutionName: string;
  PercentOfHolding: number;
  Price: number;
  TotalHoldingValue: number;
  Quantity: number;
  y: number;
  percent: number;
  id: number;
  drilldown: boolean;
  order: number;
  color: string;
}

export interface DiversificationHoldingsResponse {
  Records: DiversificationHoldingItem[];
  TotalLength: number;
  SummedValues: SummedValue[];
}

export interface DiversificationScheme {
  DiversificationId: number;
  DiversificationCategoryId?: number;
  ParentId: number;
  Color: string;
  Name: string;
  SortOrder: number;
}

export interface DiversificationSchemeResponse {
  Records: DiversificationScheme[];
}

// ===========================================
// Holding Diversification Settings Types
// ===========================================
export interface HoldingDiversificationSettingItem {
  HoldingId: number;
  Name: string;
  AccountName: string;
  AccountId: number;
  InstitutionName: string;
  InstitutionId: number;
  Balance: number;
  HasDiversification: boolean;
  IsManualAccount: boolean;
  IsIncluded: boolean;
  CanDiversify: boolean;
  ValueOverride?: boolean | null;
}

export interface HoldingDiversificationSettingsRequest {
  sort?: {
    Property: string;
    Direction: string;
    Order: number;
  };
}

export type HoldingDiversificationSettingsResponse =
  RecordResponse<HoldingDiversificationSettingItem>;

export interface UpdateHoldingDiversificationSettingArgs {
  holdingId: number;
  setting: HoldingDiversificationSettingItem;
}

// ===========================================

interface FlattenedDiversificationResult {
  flattenedList: DiversificationItem[];
  maxDepth: number;
}

export function flattenDiversificationDataForGrouping(
  allResponses: DiversificationDataById,
): FlattenedDiversificationRow[] {
  // Recursively traverse from a given diversificationId,
  // carrying down an array of ancestor names
  function traverse(
    currentId: number,
    ancestorNames: string[],
  ): FlattenedDiversificationRow[] {
    const response = allResponses[currentId];
    if (!response) {
      return [];
    }

    // Flatten each record in the current response
    return response.Records.flatMap((record) => {
      // Build the new "path" array
      const newAncestorNames = [...ancestorNames, record.name];

      const flattenedRow: FlattenedDiversificationRow = {
        ...record,
        ancestorNames: newAncestorNames,
      };

      // If this record has more children, recurse
      // (i.e. record.drilldown = true => we fetched data for record.id).
      if (record.drilldown && allResponses[record.id] !== undefined) {
        // gather the children, then combine with the current row
        return [flattenedRow, ...traverse(record.id, newAncestorNames)];
      }

      // no children, return just this row
      return [flattenedRow];
    });
  }

  // Start from the "root" diversificationId = 0
  return traverse(0, []);
}

/**
 * Flattens a nested diversification data structure with incrementing level indicators
 *
 * @param data - The nested diversification data from getRecursiveDiversification
 * @returns Object containing flattened list and maximum depth encountered
 */
export function flattenDiversificationData(
  data: DiversificationDataById,
): FlattenedDiversificationResult {
  // Result will hold all flattened items
  const flattenedList: DiversificationItem[] = [];

  // Track items we've already processed to avoid duplicates
  const processedIds = new Set<number>();

  // Track the maximum depth encountered
  let maxDepth = 0;

  // Build a map of parent-child relationships to determine the hierarchy
  const childToParent = new Map<number, number>();

  // First, build the parent-child relationship map
  Object.keys(data).forEach((keyStr) => {
    const key = Number(keyStr);
    const response = data[key];

    response?.Records.forEach((item) => {
      if (item.drilldown) {
        // If this item has drilldown and its ID is a key in our data,
        // then we know the parent-child relationship
        if (data[item.ParentId] !== undefined) {
          childToParent.set(item.id, item.ParentId);
        }
      }
    });
  });

  // Find root keys - those that don't appear as children in our map
  const rootKeys = Object.keys(data)
    .map(Number)
    .filter((key) => !childToParent.has(key));

  // Map to store the full ancestry path for each item
  const itemAncestry = new Map<
    number,
    { level: number; diversificationId: number }[]
  >();

  // Enhanced process function with better tracing
  const processItems = (
    items: DiversificationItem[] = [],
    depth: number,
    ancestors: { level: number; diversificationId: number }[] = [],
    path: string = 'root',
  ) => {
    // Track the max depth we've seen
    maxDepth = Math.max(maxDepth, depth);

    items.forEach((item) => {
      // Skip if we've already processed this item
      if (processedIds.has(item.id)) {
        return;
      }

      // Mark as processed
      processedIds.add(item.id);

      // Create the current level entry
      const currentLevelEntry = {
        level: depth,
        diversificationId: item.DiversificationId,
      };

      // Combine ancestors with current level to form the complete ancestry path
      const fullAncestry = [...ancestors, currentLevelEntry];

      // Store the ancestry for this item
      itemAncestry.set(item.id, fullAncestry);

      // Create a modified copy with the appropriate depth indicator for all levels
      const modifiedItem: DiversificationItemWithLevels = {
        ...item,
      };

      // Add all ancestor DiversificationIds
      fullAncestry.forEach((ancestor) => {
        // Use type assertion to handle dynamic property assignment
        const propertyName: DiversificationLevelProperty = `DiversificationId${String(
          ancestor.level,
        )}`;
        modifiedItem[propertyName] = ancestor.diversificationId;
      });

      // Add to our flattened list
      flattenedList.push(modifiedItem);

      // Process children if this item has a drilldown
      const childResponse = data[item.id];
      if (item.drilldown && childResponse !== undefined) {
        const childPath = `${path} > ${String(item.id)}`;
        processItems(childResponse.Records, depth + 1, fullAncestry, childPath);
      }
    });
  };

  // Process all trees starting from root keys
  rootKeys.forEach((rootKey) => {
    processItems(data[rootKey]?.Records, 1, [], `rootKey=${String(rootKey)}`);
  });

  return { flattenedList, maxDepth };
}

export interface DiversificationLevel {
  id: number;
  name: string;
}
export interface DiversificationLevelsResponse {
  Records: DiversificationLevel[];
}

// ======================
// 2. API Definition
// ======================
export const diversificationsApi = createApi({
  reducerPath: 'diversificationsApi',
  baseQuery: waServicesQuery,
  keepUnusedDataFor: 600,
  endpoints: (builder) => ({
    getDiversificationCategories: builder.query<
      DiversificationCategoriesResponse,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void to allow no-argument generated hooks.
      void
    >({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);

        const result = await baseQuery({
          url: `/api/${currentRole}/diversificationCategories`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as DiversificationCategoriesResponse };
      },
      extraOptions: { maxRetries: 3 },
    }),

    getDiversificationHoldings: builder.query<
      DiversificationHoldingsResponse,
      {
        diversificationId?: number;
        asOfDate?: string;
        investorId?: number;
        sort?: { Property: string; Direction: string; Order: number };
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
        ExcludedInstitutions?: number[];
      }
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const currentRole = getApiRoleSegment(getState() as WARootState);

        const isAdvisor = isAdvisorRole(currentRole as Role);
        const scopedToClient =
          Number.isSafeInteger(args.investorId) && Number(args.investorId) > 0;
        if (isAdvisor !== scopedToClient) {
          return { error: advisorScopeMissingError() };
        }

        const params: QueryParams = {
          DiversificationId: args.diversificationId,
          asOfDate: args.asOfDate,
          ...(args.ExcludedAccounts && args.ExcludedAccounts.length
            ? { ExcludedAccounts: args.ExcludedAccounts.join(',') }
            : {}),
          ...(args.ExcludedAccountTypes && args.ExcludedAccountTypes.length
            ? { ExcludedAccountTypes: args.ExcludedAccountTypes.join(',') }
            : {}),
          ...(args.ExcludedCategories && args.ExcludedCategories.length
            ? { ExcludedCategories: args.ExcludedCategories.join(',') }
            : {}),
          ...(args.ExcludedInstitutions && args.ExcludedInstitutions.length
            ? { ExcludedInstitutions: args.ExcludedInstitutions.join(',') }
            : {}),
        };
        if (args.sort) {
          params['sort[0].Property'] = args.sort.Property;
          params['sort[0].Direction'] = args.sort.Direction;
          params['sort[0].Order'] = args.sort.Order;
        }

        const url =
          isAdvisor && scopedToClient
            ? `${buildRoleAwarePrefix(currentRole as Role, args.investorId)}/DiversificationHoldings`
            : `/api/${currentRole}/diversificationHoldings`;

        const result = await baseQuery({
          url,
          method: 'GET',
          params,
        });

        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as DiversificationHoldingsResponse };
      },
      extraOptions: { maxRetries: 3 },
    }),

    getHistoricalDiversification: builder.query<
      HistoricalDiversificationResponse,
      HistoricalDiversificationRequest
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);
        const mergedArgs = mergeViewingInvestorTagIntoDiversificationArg(
          args,
          confirmedViewingTagSelection(state),
        );
        // Convert incoming dates to M/D/YYYY without timezone shifts
        const toMDY = (dateStr: string): string => {
          if (!dateStr) return dateStr;
          // If ISO format (YYYY-MM-DD), convert directly to M/D/YYYY
          if (dateStr.includes('-')) {
            const [yyyy, mm, dd] = dateStr.split('-');
            return `${String(Number(mm))}/${String(Number(dd))}/${yyyy}`;
          }
          // Assume already M/D/YYYY
          return dateStr;
        };

        const startDateString = toMDY(mergedArgs.startDate);
        const endDateString = toMDY(mergedArgs.endDate);

        const url = `/api/${currentRole}/historicalDiversification`;

        const result = await baseQuery({
          url,
          method: 'GET',
          params: {
            startDate: startDateString,
            endDate: endDateString,
            diversificationId: mergedArgs.diversificationId,
            ...(typeof mergedArgs.InvestorId !== 'undefined'
              ? { InvestorId: mergedArgs.InvestorId }
              : {}),
            ...(typeof mergedArgs.InvestorTagId !== 'undefined'
              ? { InvestorTagId: mergedArgs.InvestorTagId }
              : {}),
            ...(typeof mergedArgs.TagId !== 'undefined'
              ? { TagId: mergedArgs.TagId }
              : {}),
            ...(mergedArgs.ExcludedAccounts &&
            mergedArgs.ExcludedAccounts.length
              ? { ExcludedAccounts: mergedArgs.ExcludedAccounts.join(',') }
              : {}),
            ...(mergedArgs.ExcludedAccountTypes &&
            mergedArgs.ExcludedAccountTypes.length
              ? {
                  ExcludedAccountTypes:
                    mergedArgs.ExcludedAccountTypes.join(','),
                }
              : {}),
            ...(mergedArgs.ExcludedCategories &&
            mergedArgs.ExcludedCategories.length
              ? { ExcludedCategories: mergedArgs.ExcludedCategories.join(',') }
              : {}),
            ...(mergedArgs.ExcludedInstitutions &&
            mergedArgs.ExcludedInstitutions.length
              ? {
                  ExcludedInstitutions:
                    mergedArgs.ExcludedInstitutions.join(','),
                }
              : {}),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as HistoricalDiversificationResponse };
      },
      providesTags: ['HistoricalDiversification'],
      extraOptions: { maxRetries: 3 },
    }),

    getDiversification: builder.query<
      DiversificationResponse,
      DiversificationRequest
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);
        const mergedArgs = mergeViewingInvestorTagIntoDiversificationArg(
          args,
          confirmedViewingTagSelection(state),
        );
        const url = `/api/${currentRole}/diversification`;

        const result = await baseQuery({
          url,
          method: 'GET',
          params: {
            ...mergedArgs,
            categoryId: mergedArgs.categoryId ?? 0,
            ...(typeof mergedArgs.InvestorId !== 'undefined'
              ? { InvestorId: mergedArgs.InvestorId }
              : {}),
            ...(typeof mergedArgs.InvestorTagId !== 'undefined'
              ? { InvestorTagId: mergedArgs.InvestorTagId }
              : {}),
            ...(typeof mergedArgs.TagId !== 'undefined'
              ? { TagId: mergedArgs.TagId }
              : {}),
            ...(mergedArgs.ExcludedAccounts &&
            mergedArgs.ExcludedAccounts.length
              ? { ExcludedAccounts: mergedArgs.ExcludedAccounts.join(',') }
              : {}),
            ...(mergedArgs.ExcludedAccountTypes &&
            mergedArgs.ExcludedAccountTypes.length
              ? {
                  ExcludedAccountTypes:
                    mergedArgs.ExcludedAccountTypes.join(','),
                }
              : {}),
            ...(mergedArgs.ExcludedCategories &&
            mergedArgs.ExcludedCategories.length
              ? { ExcludedCategories: mergedArgs.ExcludedCategories.join(',') }
              : {}),
            ...(mergedArgs.ExcludedInstitutions &&
            mergedArgs.ExcludedInstitutions.length
              ? {
                  ExcludedInstitutions:
                    mergedArgs.ExcludedInstitutions.join(','),
                }
              : {}),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as DiversificationResponse };
      },
      providesTags: (_result, _error, args) => [
        {
          type: 'Diversification',
          id: `portfolio-${String(args.diversificationId)}-${String(
            args.categoryId ?? 0,
          )}`,
        },
        { type: 'Diversification', id: 'LIST' },
      ],
      extraOptions: { maxRetries: 3 },
    }),

    getDiversificationSchemes: builder.query<
      DiversificationSchemeResponse,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void to allow no-argument generated hooks.
      { hideToBeClassified?: boolean } | void // Allow args or no args
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);
        const params: QueryParams = {};
        if (args?.hideToBeClassified) {
          params.hideToBeClassified = args.hideToBeClassified;
        }

        const result = await baseQuery({
          url: `/api/${currentRole}/diversifications`,
          method: 'GET',
          params,
        });
        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as DiversificationSchemeResponse };
      },
    }),

    getAccountDiversification: builder.query<
      DiversificationResponse,
      {
        accountId: number;
        asOfDate?: string;
        diversificationId?: number;
        categoryId?: number;
        /**
         * When true (root requests only), ask the API to group the root account allocation
         * by SubClassification so manual/offline-style accounts show a single top-level slice.
         *
         * NOTE: Grouped rows do not include diversification tree identifiers, so callers that
         * need drilldown must map `name` back to a diversification scheme node id.
         */
        groupBySubClassification?: boolean;
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        // Use role from state and ensure lowercase to match the required URL format.
        const currentRole = 'investor';

        const result = await baseQuery({
          // The accountId goes directly into the URL path.
          url: `/api/${currentRole}/AccountDiversification/${String(
            args.accountId,
          )}`,
          method: 'GET',
          params: buildAccountDiversificationRequestParams(args),
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as DiversificationResponse };
      },
      providesTags: (_result, _error, args) => [
        {
          type: 'Diversification',
          id: getAccountDiversificationCacheTagId(args),
        },
        { type: 'Diversification', id: 'LIST' },
      ],
      extraOptions: { maxRetries: 3 },
    }),

    // New endpoint for holding diversification settings
    getHoldingDiversificationSettings: builder.query<
      HoldingDiversificationSettingsResponse,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void to allow no-argument generated hooks.
      HoldingDiversificationSettingsRequest | void // Allow no args for default sort
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);
        const params: QueryParams = {};
        if (args?.sort) {
          params['sort[0].Property'] = args.sort.Property;
          params['sort[0].Direction'] = args.sort.Direction;
          params['sort[0].Order'] = args.sort.Order;
        } else {
          // Default sort if not provided, as per curl example
          params['sort[0].Property'] = 'Name';
          params['sort[0].Direction'] = 'asc';
          params['sort[0].Order'] = 0;
        }

        const result = await baseQuery({
          url: `/api/${currentRole}/holdingDiversificationSettings`,
          method: 'GET',
          params,
        });

        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as HoldingDiversificationSettingsResponse };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.Records.map(({ HoldingId }) => ({
                type: 'HoldingDiversificationSetting' as const,
                id: HoldingId,
              })),
              { type: 'HoldingDiversificationSetting', id: 'LIST' },
            ]
          : [{ type: 'HoldingDiversificationSetting', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    updateHoldingDiversificationSetting: builder.mutation<
      boolean, // Response is true on success
      UpdateHoldingDiversificationSettingArgs
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);

        const result = await baseQuery({
          url: `/api/${currentRole}/holdingDiversificationSettings/${String(
            args.holdingId,
          )}`,
          method: 'PUT',
          body: args.setting, // body of the request, changed from 'data' to 'body'
        });

        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as boolean };
      },
      invalidatesTags: (_result, _error, args) => [
        { type: 'HoldingDiversificationSetting', id: args.holdingId },
        { type: 'HoldingDiversificationSetting', id: 'LIST' }, // To refresh the list view if displayed elsewhere
      ],
      extraOptions: { maxRetries: 1 }, // Mutations might not need as many retries
    }),

    getDiversificationLevels: builder.query<
      DiversificationLevelsResponse,
      { accountId: number }
    >({
      queryFn: async (args, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = getApiRoleSegment(state);
        const url = `/api/${currentRole}/diversificationLevels`;
        const result = await baseQuery({
          url,
          method: 'GET',
          params: {
            diversificationSubject: 'Account',
            accountId: args.accountId,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as DiversificationLevelsResponse };
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
  // Add tagTypes if not already present or if 'HoldingDiversificationSetting' is new
  tagTypes: [
    'DiversificationCategory',
    'HistoricalDiversification',
    'Diversification',
    'HoldingDiversificationSetting', // Added new tag type
  ],
});

export const {
  useGetDiversificationCategoriesQuery,
  useGetHistoricalDiversificationQuery:
    useGetHistoricalDiversificationQueryBase,
  useGetDiversificationQuery: useGetDiversificationQueryBase,
  useGetDiversificationHoldingsQuery,
  useGetDiversificationSchemesQuery,
  useGetAccountDiversificationQuery,
  useGetHoldingDiversificationSettingsQuery, // Export new query hook
  useUpdateHoldingDiversificationSettingMutation, // Export new mutation hook
  useGetDiversificationLevelsQuery,
} = diversificationsApi;

/**
 * The subscription options a wrapped diversification query takes (skip,
 * polling, refetch-on-mount). Spelled out rather than read off the endpoint's
 * `useQuery` with `Parameters`, which loses the result type and made every
 * caller's `data` an `any`.
 */
type DiversificationQueryOptions = TypedUseQuerySubscriptionOptions;

export function useGetHistoricalDiversificationQuery(
  arg: HistoricalDiversificationRequest | typeof skipToken,
  options?: DiversificationQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildPortfolioDiversificationSubscriptionArg(arg, confirmed.selection);

  return diversificationsApi.endpoints.getHistoricalDiversification.useQuery(
    queryArg,
    options,
  );
}

export function useGetDiversificationQuery(
  arg: DiversificationRequest | typeof skipToken,
  options?: DiversificationQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildPortfolioDiversificationSubscriptionArg(arg, confirmed.selection);

  return diversificationsApi.endpoints.getDiversification.useQuery(
    queryArg,
    options,
  );
}
